import { useQuery } from '@tanstack/react-query';
import { collection, doc, getDocs, getDoc } from 'firebase/firestore';
import firebaseApp from '../firebase-app';
import { FS_PATH_APP_CONFIGS, FS_PATH_TEAMS } from '../constants';
import { flatten, keyBy } from 'lodash';
import adminApi from '../api/admin-api';
import { Squad, SquadPlayer } from '../components/scenes/all-players/types';
import { createSquadPlayer } from '../components/scenes/squad-database/hooks/player';
import { User } from 'firebase/auth';
import { useUserAuth } from '../contexts/auth';

export const getTeams = async (): Promise<any[]> => {
  const querySnapshot = await getDocs(collection(firebaseApp.db, FS_PATH_TEAMS));
  const teams: any = [];
  querySnapshot.forEach((doc) => {
    const data = doc.data();
    teams.push({
      // abbrv: doc.id,
      ...data,
    });
  });
  return teams;
};

export function useAllTeams() {
  const res = useQuery({
    queryKey: ['all-teams'],
    queryFn: async () => {
      const res = await getTeams();
      return res;
    },
  });
  return res;
}
const getTeamsConfig = async (): Promise<any[]> => {
  const querySnapshot = await getDocs(collection(firebaseApp.db, FS_PATH_APP_CONFIGS));
  const teams: any = [];
  querySnapshot.forEach((doc) => {
    const data = doc.data();
    if (data.appType === 'club') {
      teams.push({
        abbrv: doc.id,
        ...data,
      });
    }
  });
  return teams;
};
export function useAllPlayers(params: { season: string; currentUser: User | null }) {
  const { season } = params;
  const { currentUser } = useUserAuth();

  const fetchSquad = async (
    teamId: string,
    season: string,
    full = true,
    skipCache = true
  ): Promise<SquadPlayer[]> => {
    let squadPlayers: SquadPlayer[] = [];

    try {
      const response = await adminApi.get({
        path: `/squad/${teamId}/${season}?full=${full}&skipCache=${skipCache}`,
        auth: await currentUser?.getIdToken(),
      });

      if (response.status === 404) {
        return [];
      }

      if (!response.ok) {
        throw new Error(`Fetching squad failed! (${response.status})`);
      }

      const squad: Squad = await response.json();

      if (squad?.squad) {
        squadPlayers = squad.squad.map((player) => createSquadPlayer(teamId, player));
      }
    } catch (error) {
      console.error(error);

      throw error;
    }

    return squadPlayers;
  };
  return useQuery({
    queryKey: ['all-players', season],
    queryFn: async () => {
      const teamsConfig = await getTeamsConfig();

      const teamsConfigByKey = keyBy(teamsConfig || [], 'abbrv');
      const teamsSquads = await Promise.all(
        teamsConfig.map(async (team) => fetchSquad(team.abbrv, season).catch(() => []))
      );

      return {
        teamsConfig,
        teamsSquads: flatten(teamsSquads)
          .filter((player) => player?.activeCurrentClub?.id)
          .map((player) => ({
            ...player,
            activeCurrentClub: {
              ...player.activeCurrentClub,
              league: (() => {
                const key = player.activeCurrentClub?.id;
                if (!key) {
                  return;
                }
                const teamConfig = teamsConfigByKey[key];
                return teamConfig?.configLeagueName;
              })(),
            },
          })),
      };
    },
    refetchOnWindowFocus: false,
  });
}
